import { useState, useEffect } from 'react';
import { ChevronRight, Check, ArrowLeft, Radio } from 'lucide-react';
import { apiFetch } from '../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DbEntry { id: number; name: string; }
interface Company { company_name: string; company_api_id: number; db_id: number; }
interface Area { area_name: string; area_api_id: number; area_id: number; }
interface Machine { machine_code: string; machine_name: string; }
interface Point { point_index: number; point_name: string | null; }

type AxisLabel = 'H' | 'V' | 'A';
interface AxisMapping { x: AxisLabel; y: AxisLabel; z: AxisLabel; }

type Step = 'code' | 'location' | 'axes' | 'done';

// Phantom codes are 9-digit integers
function isValidCode(code: string): boolean {
  return /^\d{9}$/.test(code.trim());
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

const STEPS: { id: Step; label: string }[] = [
  { id: 'code',     label: 'Device code' },
  { id: 'location', label: 'Select point' },
  { id: 'axes',     label: 'Axis mapping' },
];

function StepBar({ current }: { current: Step }) {
  const idx = STEPS.findIndex(s => s.id === current);
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <div key={s.id} className="flex items-center gap-0">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                done   ? 'bg-[#00D4BE] text-white' :
                active ? 'bg-[#00D4BE]/20 text-[#00D4BE] border-2 border-[#00D4BE]' :
                         'bg-gray-100 text-gray-400'
              }`}>
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-sm font-medium ${active ? 'text-gray-800' : done ? 'text-[#00D4BE]' : 'text-gray-400'}`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`w-12 h-0.5 mx-3 ${done ? 'bg-[#00D4BE]' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cascading select helper
// ---------------------------------------------------------------------------

function SelectRow<T>({
  label,
  value,
  options,
  disabled,
  onSelect,
  getKey,
  getLabel,
}: {
  label: string;
  value: T | null;
  options: T[];
  disabled?: boolean;
  onSelect: (v: T) => void;
  getKey: (v: T) => string;
  getLabel: (v: T) => string;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</label>
      <select
        disabled={disabled || options.length === 0}
        value={value ? getKey(value) : ''}
        onChange={e => {
          const found = options.find(o => getKey(o) === e.target.value);
          if (found) onSelect(found);
        }}
        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00D4BE]/60 transition-colors disabled:opacity-50"
      >
        <option value="">Select {label.toLowerCase()}…</option>
        {options.map(o => (
          <option key={getKey(o)} value={getKey(o)}>{getLabel(o)}</option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Device code
// ---------------------------------------------------------------------------

function StepCode({
  code,
  onChange,
  onNext,
}: {
  code: string;
  onChange: (v: string) => void;
  onNext: () => void;
}) {
  const valid = isValidCode(code);
  const touched = code.length > 0;

  return (
    <div className="max-w-sm">
      <h2 className="text-base font-semibold text-gray-800 mb-1">Enter device code</h2>
      <p className="text-sm text-gray-500 mb-5">
        The phantom code is the 9-digit serial number printed on the sensor.
      </p>
      <div className="space-y-4">
        <div>
          <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Phantom Code</label>
          <div className="relative">
            <Radio className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={code}
              onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 9))}
              placeholder="e.g. 189303885"
              maxLength={9}
              className={`w-full pl-9 pr-3 py-2.5 bg-gray-50 border rounded-lg text-sm font-mono focus:outline-none transition-colors ${
                touched && !valid ? 'border-red-300 focus:border-red-400' :
                valid ? 'border-green-300 focus:border-green-400' :
                'border-gray-200 focus:border-[#00D4BE]/60'
              }`}
            />
            {valid && (
              <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
            )}
          </div>
          {touched && !valid && (
            <p className="text-xs text-red-500 mt-1">Must be exactly 9 digits.</p>
          )}
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={!valid}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors disabled:opacity-50"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Select point from hierarchy
// ---------------------------------------------------------------------------

function StepLocation({
  onSelect,
  onBack,
}: {
  onSelect: (params: { db: DbEntry; company: Company; area: Area; machine: Machine; point: Point }) => void;
  onBack: () => void;
}) {
  const [databases, setDatabases] = useState<DbEntry[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [points, setPoints] = useState<Point[]>([]);

  const [selDb, setSelDb] = useState<DbEntry | null>(null);
  const [selCo, setSelCo] = useState<Company | null>(null);
  const [selArea, setSelArea] = useState<Area | null>(null);
  const [selMachine, setSelMachine] = useState<Machine | null>(null);
  const [selPoint, setSelPoint] = useState<Point | null>(null);

  useEffect(() => { apiFetch('/setup/databases').then(setDatabases).catch(() => {}); }, []);

  useEffect(() => {
    if (!selDb) { setCompanies([]); setSelCo(null); return; }
    apiFetch(`/setup/companies?db_id=${selDb.id}`)
      .then(d => { setCompanies(d); setSelCo(null); setAreas([]); setSelArea(null); setMachines([]); setSelMachine(null); setPoints([]); setSelPoint(null); })
      .catch(() => {});
  }, [selDb]);

  useEffect(() => {
    if (!selDb || !selCo) { setAreas([]); setSelArea(null); return; }
    apiFetch(`/setup/areas?db_id=${selDb.id}&company_name=${encodeURIComponent(selCo.company_name)}`)
      .then(d => { setAreas(d); setSelArea(null); setMachines([]); setSelMachine(null); setPoints([]); setSelPoint(null); })
      .catch(() => {});
  }, [selCo, selDb]);

  useEffect(() => {
    if (!selDb || !selCo || !selArea) { setMachines([]); setSelMachine(null); return; }
    apiFetch(`/setup/machines-list?db_id=${selDb.id}&company_name=${encodeURIComponent(selCo.company_name)}&area_name=${encodeURIComponent(selArea.area_name)}`)
      .then(d => { setMachines(d); setSelMachine(null); setPoints([]); setSelPoint(null); })
      .catch(() => {});
  }, [selArea, selCo, selDb]);

  useEffect(() => {
    if (!selMachine) { setPoints([]); setSelPoint(null); return; }
    apiFetch(`/setup/points-list?machine_code=${selMachine.machine_code}`)
      .then(d => { setPoints(d); setSelPoint(null); })
      .catch(() => {});
  }, [selMachine]);

  const canProceed = !!(selDb && selCo && selArea && selMachine && selPoint);

  return (
    <div className="max-w-sm">
      <h2 className="text-base font-semibold text-gray-800 mb-1">Select measurement point</h2>
      <p className="text-sm text-gray-500 mb-5">
        Choose which machine point this sensor is installed on.
      </p>
      <div className="space-y-3">
        <SelectRow
          label="Database"
          value={selDb}
          options={databases}
          onSelect={setSelDb}
          getKey={d => String(d.id)}
          getLabel={d => d.name}
        />
        <SelectRow
          label="Company"
          value={selCo}
          options={companies}
          disabled={!selDb}
          onSelect={setSelCo}
          getKey={c => c.company_name}
          getLabel={c => c.company_name}
        />
        <SelectRow
          label="Area"
          value={selArea}
          options={areas}
          disabled={!selCo}
          onSelect={setSelArea}
          getKey={a => String(a.area_id)}
          getLabel={a => a.area_name}
        />
        <SelectRow
          label="Machine"
          value={selMachine}
          options={machines}
          disabled={!selArea}
          onSelect={setSelMachine}
          getKey={m => m.machine_code}
          getLabel={m => m.machine_name}
        />
        <SelectRow
          label="Point"
          value={selPoint}
          options={points}
          disabled={!selMachine}
          onSelect={setSelPoint}
          getKey={p => String(p.point_index)}
          getLabel={p => p.point_name ? `${p.point_index} — ${p.point_name}` : `Point ${p.point_index}`}
        />

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            disabled={!canProceed}
            onClick={() => onSelect({ db: selDb!, company: selCo!, area: selArea!, machine: selMachine!, point: selPoint! })}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors disabled:opacity-50"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Axis mapping
// ---------------------------------------------------------------------------

const AXIS_OPTIONS: AxisLabel[] = ['H', 'V', 'A'];

const AXIS_DESCRIPTIONS: Record<AxisLabel, string> = {
  H: 'Horizontal',
  V: 'Vertical',
  A: 'Axial',
};

function AxisSelect({
  channel,
  value,
  onChange,
  usedBy,
}: {
  channel: 'X' | 'Y' | 'Z';
  value: AxisLabel;
  onChange: (v: AxisLabel) => void;
  usedBy: Record<AxisLabel, string>;
}) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
      <div className="w-16 text-center">
        <span className="text-2xl font-bold text-gray-700">{channel}</span>
        <p className="text-xs text-gray-400">Channel</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
      <div className="flex gap-2 flex-1">
        {AXIS_OPTIONS.map(axis => {
          const takenBy = usedBy[axis];
          const isTaken = takenBy && takenBy !== channel;
          const isSelected = value === axis;
          return (
            <button
              key={axis}
              type="button"
              disabled={!!isTaken}
              onClick={() => onChange(axis)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors border ${
                isSelected
                  ? 'bg-[#00D4BE] text-white border-[#00D4BE]'
                  : isTaken
                  ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#00D4BE]/40 hover:text-[#00D4BE]'
              }`}
            >
              {axis}
              <span className={`block text-xs font-normal ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>
                {AXIS_DESCRIPTIONS[axis]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepAxes({
  mapping,
  onChange,
  onBack,
  onSave,
  saving,
}: {
  mapping: AxisMapping;
  onChange: (m: AxisMapping) => void;
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  // Build reverse map: axis label → which channel has it
  const usedBy: Record<AxisLabel, string> = {
    H: Object.entries(mapping).find(([, v]) => v === 'H')?.[0]?.toUpperCase() ?? '',
    V: Object.entries(mapping).find(([, v]) => v === 'V')?.[0]?.toUpperCase() ?? '',
    A: Object.entries(mapping).find(([, v]) => v === 'A')?.[0]?.toUpperCase() ?? '',
  };

  const setChannel = (ch: 'x' | 'y' | 'z', axis: AxisLabel) => {
    // Swap if target axis already assigned to another channel
    const current = mapping[ch];
    const swapCh = (Object.entries(mapping) as [string, AxisLabel][]).find(([k, v]) => v === axis && k !== ch)?.[0] as 'x' | 'y' | 'z' | undefined;
    const next = { ...mapping, [ch]: axis };
    if (swapCh) next[swapCh] = current;
    onChange(next);
  };

  return (
    <div className="max-w-sm">
      <h2 className="text-base font-semibold text-gray-800 mb-1">Configure axis mapping</h2>
      <p className="text-sm text-gray-500 mb-5">
        Map each sensor channel to the physical axis direction on this machine.
      </p>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 mb-5">
        <AxisSelect channel="X" value={mapping.x} onChange={v => setChannel('x', v)} usedBy={usedBy} />
        <AxisSelect channel="Y" value={mapping.y} onChange={v => setChannel('y', v)} usedBy={usedBy} />
        <AxisSelect channel="Z" value={mapping.z} onChange={v => setChannel('z', v)} usedBy={usedBy} />
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save Device'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Done state
// ---------------------------------------------------------------------------

function StepDone({
  phantomCode,
  machineName,
  pointName,
  mapping,
  onAddAnother,
}: {
  phantomCode: string;
  machineName: string;
  pointName: string;
  mapping: AxisMapping;
  onAddAnother: () => void;
}) {
  return (
    <div className="max-w-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
          <Check className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-800">Device registered</h2>
          <p className="text-sm text-gray-500">Phantom {phantomCode}</p>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg border border-gray-200 px-4 py-3 space-y-1.5 text-sm mb-5">
        <div className="flex justify-between">
          <span className="text-gray-500">Machine</span>
          <span className="text-gray-800 font-medium">{machineName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Point</span>
          <span className="text-gray-800 font-medium">{pointName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Axis map</span>
          <span className="text-gray-800 font-medium font-mono">X→{mapping.x} · Y→{mapping.y} · Z→{mapping.z}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onAddAnother}
        className="px-5 py-2.5 bg-[#00D4BE] text-white rounded-lg font-medium text-sm hover:bg-[#00bfab] transition-colors"
      >
        Add another device
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AddDevice() {
  const [step, setStep] = useState<Step>('code');
  const [code, setCode] = useState('');
  const [location, setLocation] = useState<{
    db: DbEntry; company: Company; area: Area; machine: Machine; point: Point;
  } | null>(null);
  const [mapping, setMapping] = useState<AxisMapping>({ x: 'H', y: 'V', z: 'A' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setStep('code');
    setCode('');
    setLocation(null);
    setMapping({ x: 'H', y: 'V', z: 'A' });
    setError('');
  };

  const handleSave = async () => {
    if (!location) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch('/setup/devices', {
        method: 'POST',
        body: JSON.stringify({
          phantom_code: code.trim(),
          machine_code: location.machine.machine_code,
          point_index: location.point.point_index,
          point_name: location.point.point_name,
          db_id: location.db.id,
          axis_mapping: mapping,
        }),
      });
      setStep('done');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save device.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-xl font-semibold text-gray-800 mb-6">Add Device</h1>

      {step !== 'done' && <StepBar current={step} />}

      {error && (
        <div className="mb-5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      {step === 'code' && (
        <StepCode
          code={code}
          onChange={setCode}
          onNext={() => setStep('location')}
        />
      )}

      {step === 'location' && (
        <StepLocation
          onSelect={loc => { setLocation(loc); setStep('axes'); }}
          onBack={() => setStep('code')}
        />
      )}

      {step === 'axes' && (
        <StepAxes
          mapping={mapping}
          onChange={setMapping}
          onBack={() => setStep('location')}
          onSave={handleSave}
          saving={saving}
        />
      )}

      {step === 'done' && location && (
        <StepDone
          phantomCode={code}
          machineName={location.machine.machine_name}
          pointName={location.point.point_name ?? `Point ${location.point.point_index}`}
          mapping={mapping}
          onAddAnother={reset}
        />
      )}
    </div>
  );
}
